'use client';
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Link from 'next/link';

const PAIN_POINTS = [
  {
    emoji: '😶',
    stat: '73%',
    label: 'of remote workers feel disconnected from their team',
    source: 'Buffer State of Remote Work 2024',
  },
  {
    emoji: '🧠',
    stat: '4.2hrs',
    label: 'average daily cognitive fatigue onset for remote workers',
    source: 'Microsoft Work Trend Index',
  },
  {
    emoji: '📉',
    stat: '31%',
    label: 'productivity drop after 90 min of unbroken deep work',
    source: 'Draugiem Group Study',
  },
];

const FEATURES = [
  {
    emoji: '🏆',
    title: 'Team Leaderboard',
    desc: 'A shared scoreboard your team checks every morning. Friendly competition beats any Slack poll.',
    accent: '#ffd23f',
  },
  {
    emoji: '🔥',
    title: 'Shared Streak',
    desc: 'One streak for the whole team. Miss a day and you all lose it — the accountability loop that actually works.',
    accent: '#ff3b6b',
  },
  {
    emoji: '⚔️',
    title: '1v1 Arena Challenges',
    desc: 'Challenge a teammate to a head-to-head micro-game. 3 minutes. Bragging rights for a week.',
    accent: '#7b5bff',
  },
  {
    emoji: '📅',
    title: 'Daily Team Challenge',
    desc: 'One shared puzzle per day. Everyone plays the same game, compares scores in Slack.',
    accent: '#36e2a4',
  },
  {
    emoji: '🎮',
    title: '13 Premium Games',
    desc: 'Psychology-backed micro-games designed to reset focus — not drain it. 5 minutes, then back to work.',
    accent: '#ff9500',
  },
  {
    emoji: '📊',
    title: 'Team Analytics',
    desc: 'See who plays most, peak break times, and which games your team loves. Data for the ops-minded manager.',
    accent: '#06b6d4',
  },
];

const TESTIMONIALS = [
  {
    quote: 'We replaced our Friday Kahoot with Unbored. The team actually shows up now.',
    name: 'Priya S.',
    role: 'Engineering Manager · 14-person remote team',
    avatar: '👩‍💻',
  },
  {
    quote: 'The shared streak is ruthless. We haven\'t missed a day in 6 weeks because nobody wants to be the one who breaks it.',
    name: 'Marcus T.',
    role: 'Head of Product · Fully distributed startup',
    avatar: '👨‍🎨',
  },
  {
    quote: 'Onboarding new hires is awkward. Beating them at Gem Duel on day one is not.',
    name: 'Lena K.',
    role: 'People Ops Lead · 40-person SaaS company',
    avatar: '👩‍💼',
  },
];

const PLAN_FEATURES = [
  '25 team seats included',
  'All 13 premium micro-games',
  'Shared team streak & leaderboard',
  '1v1 Arena challenges',
  'Daily team challenge',
  'Team analytics dashboard',
  'Slack-ready share cards',
  'Priority support',
  'Cancel anytime',
];

export default function TeamsClient() {
  const router = useRouter();
  const [ctaPressed, setCtaPressed] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleCTA = () => {
    setCtaPressed(true);
    setTimeout(() => setCtaPressed(false), 300);
    // TODO: wire to Stripe checkout with team price ID
  };

  const handleWaitlist = (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailInput.trim()) return;
    setSubmitted(true);
    // TODO: wire to email capture / Supabase
  };

  return (
    <div className="relative z-10 flex flex-col min-h-screen overflow-hidden">
      {/* Animated background */}
      <div className="fixed inset-0 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 90% 50% at 50% 0%, rgba(54,226,164,0.06) 0%, rgba(123,91,255,0.04) 50%, transparent 80%)',
          }}
        />
        <div
          className="absolute rounded-full opacity-10 pulse-anim"
          style={{
            width: 500,
            height: 500,
            top: '-150px',
            right: '-150px',
            background: 'radial-gradient(circle, #36e2a4 0%, transparent 70%)',
          }}
        />
        <div
          className="absolute rounded-full opacity-8 pulse-anim"
          style={{
            width: 350,
            height: 350,
            bottom: '5%',
            left: '-100px',
            background: 'radial-gradient(circle, #7b5bff 0%, transparent 70%)',
            animationDelay: '2s',
          }}
        />
      </div>

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-5 pt-5 pb-3">
        <Link href="/" className="flex items-center gap-2">
          <AppLogo size={32} />
          <span className="font-bold text-lg tracking-tight text-foreground">Unbored</span>
        </Link>
        <Link
          href="/"
          className="text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
        >
          ← Back
        </Link>
      </header>

      <main className="relative z-10 flex flex-col items-center px-5 pb-20">

        {/* HERO */}
        <section className="w-full max-w-2xl pt-12 pb-10 text-center">
          <div
            className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-6 text-xs font-black uppercase tracking-widest"
            style={{
              background: 'rgba(54,226,164,0.12)',
              border: '1px solid rgba(54,226,164,0.35)',
              color: '#36e2a4',
            }}
          >
            🏢 Unbored for Teams
          </div>

          <h1 className="text-4xl sm:text-5xl font-extrabold leading-tight text-foreground mb-4">
            The 5-minute brain break<br />
            <span
              style={{
                background: 'linear-gradient(135deg, #36e2a4 0%, #7b5bff 60%, #ff3b6b 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              your team actually wants.
            </span>
          </h1>

          <p className="text-muted-foreground text-base sm:text-lg font-medium max-w-lg mx-auto mb-8">
            Shared streaks. Team leaderboards. 1v1 challenges. 13 psychology-backed micro-games that reset focus — not drain it. Built for remote teams who are done with mandatory fun.
          </p>

          {/* Price callout */}
          <div
            className="inline-flex flex-col items-center rounded-2xl px-8 py-5 mb-8"
            style={{
              background: 'linear-gradient(145deg, rgba(54,226,164,0.1), rgba(123,91,255,0.08))',
              border: '1px solid rgba(54,226,164,0.3)',
            }}
          >
            <div className="flex items-end gap-1 mb-1">
              <span className="text-5xl font-extrabold text-foreground">$49</span>
              <span className="text-muted-foreground text-base font-semibold mb-2">/month</span>
            </div>
            <p className="text-xs text-muted-foreground font-medium">Up to 25 seats · $1.96/person · cancel anytime</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={handleCTA}
              className="btn-primary px-8 py-4 text-base font-black rounded-2xl"
              style={{
                background: ctaPressed
                  ? 'linear-gradient(135deg, #22b87a 0%, #5a3fd4 100%)'
                  : 'linear-gradient(135deg, #36e2a4 0%, #7b5bff 100%)',
                transform: ctaPressed ? 'scale(0.97)' : 'scale(1)',
                boxShadow: '0 0 40px rgba(54,226,164,0.3)',
                transition: 'all 150ms ease',
                color: '#0a0a0f',
              }}
            >
              Start Team Trial →
            </button>
            <a
              href="#how-it-works"
              className="btn-ghost px-8 py-4 text-base font-bold rounded-2xl"
            >
              See how it works
            </a>
          </div>
        </section>

        {/* PAIN POINTS — asymmetric bento */}
        <section className="w-full max-w-2xl mb-16">
          <h2 className="text-center text-xs font-black uppercase tracking-widest text-muted-foreground mb-6">
            The remote work problem nobody talks about
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {PAIN_POINTS.map((p, i) => (
              <div
                key={p.stat}
                className="rounded-2xl p-5 flex flex-col gap-2"
                style={{
                  background: i === 1
                    ? 'linear-gradient(145deg, rgba(255,59,107,0.1), rgba(123,91,255,0.06))'
                    : 'linear-gradient(145deg, #16162a, #0f0f1e)',
                  border: i === 1
                    ? '1px solid rgba(255,59,107,0.3)'
                    : '1px solid rgba(42,42,69,0.8)',
                }}
              >
                <span className="text-2xl">{p.emoji}</span>
                <span className="text-3xl font-extrabold text-foreground">{p.stat}</span>
                <p className="text-sm text-muted-foreground font-medium leading-snug">{p.label}</p>
                <p className="text-xs opacity-40 text-muted-foreground mt-auto">{p.source}</p>
              </div>
            ))}
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section id="how-it-works" className="w-full max-w-2xl mb-16">
          <h2 className="text-2xl font-extrabold text-foreground mb-2 text-center">How it works</h2>
          <p className="text-muted-foreground text-sm text-center mb-8">No app downloads. No IT tickets. Plays entirely in the browser.</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Large card */}
            <div
              className="sm:row-span-2 rounded-2xl p-6 flex flex-col justify-between"
              style={{
                background: 'linear-gradient(145deg, rgba(54,226,164,0.1), rgba(54,226,164,0.04))',
                border: '1px solid rgba(54,226,164,0.25)',
                minHeight: 220,
              }}
            >
              <div>
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-xl mb-4"
                  style={{ background: 'rgba(54,226,164,0.15)' }}
                >
                  🔗
                </div>
                <h3 className="text-lg font-extrabold text-foreground mb-2">One link. Everyone's in.</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Drop the team link in Slack. Your team clicks, creates a free account, and they're on your shared leaderboard in under 60 seconds. No IT, no installs, no onboarding deck.
                </p>
              </div>
              <div
                className="mt-4 rounded-xl px-3 py-2 text-xs font-bold"
                style={{ background: 'rgba(54,226,164,0.12)', color: '#36e2a4' }}
              >
                unbored.com/team/your-workspace
              </div>
            </div>

            <div
              className="rounded-2xl p-5 flex items-start gap-4"
              style={{
                background: 'linear-gradient(145deg, #16162a, #0f0f1e)',
                border: '1px solid rgba(42,42,69,0.8)',
              }}
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
                style={{ background: 'rgba(255,210,63,0.12)' }}
              >
                ☀️
              </div>
              <div>
                <h3 className="text-sm font-extrabold text-foreground mb-1">Daily challenge drops at 9am</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">Same game, same rules, everyone plays. Scores post to the team board automatically.</p>
              </div>
            </div>

            <div
              className="rounded-2xl p-5 flex items-start gap-4"
              style={{
                background: 'linear-gradient(145deg, rgba(123,91,255,0.1), rgba(123,91,255,0.04))',
                border: '1px solid rgba(123,91,255,0.25)',
              }}
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
                style={{ background: 'rgba(123,91,255,0.15)' }}
              >
                ⚔️
              </div>
              <div>
                <h3 className="text-sm font-extrabold text-foreground mb-1">Challenge a teammate anytime</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">Send a 1v1 challenge link. They have 24 hours to respond. Loser buys coffee.</p>
              </div>
            </div>
          </div>
        </section>

        {/* FEATURES — bento grid */}
        <section className="w-full max-w-2xl mb-16">
          <h2 className="text-2xl font-extrabold text-foreground mb-2 text-center">Everything in the $49 plan</h2>
          <p className="text-muted-foreground text-sm text-center mb-8">One flat price. No per-seat upsells. No feature gating.</p>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                className={`rounded-2xl p-4 flex flex-col gap-2 ${i === 0 ? 'col-span-2 sm:col-span-1' : ''}`}
                style={{
                  background: 'linear-gradient(145deg, #16162a, #0f0f1e)',
                  border: `1px solid ${f.accent}22`,
                }}
              >
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center text-lg"
                  style={{ background: `${f.accent}18` }}
                >
                  {f.emoji}
                </div>
                <h3 className="text-sm font-extrabold text-foreground">{f.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="w-full max-w-2xl mb-16">
          <h2 className="text-2xl font-extrabold text-foreground mb-8 text-center">What remote teams say</h2>
          <div className="flex flex-col gap-4">
            {TESTIMONIALS.map((t, i) => (
              <div
                key={t.name}
                className="rounded-2xl p-5"
                style={{
                  background: i === 1
                    ? 'linear-gradient(145deg, rgba(123,91,255,0.1), rgba(123,91,255,0.04))'
                    : 'linear-gradient(145deg, #16162a, #0f0f1e)',
                  border: i === 1
                    ? '1px solid rgba(123,91,255,0.3)'
                    : '1px solid rgba(42,42,69,0.8)',
                  marginLeft: i === 1 ? '1.5rem' : '0',
                  marginRight: i === 1 ? '0' : '1.5rem',
                }}
              >
                <p className="text-sm font-semibold text-foreground leading-relaxed mb-4">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <div className="flex items-center gap-3">
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center text-lg flex-shrink-0"
                    style={{ background: 'rgba(255,255,255,0.06)' }}
                  >
                    {t.avatar}
                  </div>
                  <div>
                    <p className="text-xs font-bold text-foreground">{t.name}</p>
                    <p className="text-xs text-muted-foreground">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* PRICING CARD */}
        <section className="w-full max-w-md mb-16">
          <div
            className="rounded-3xl p-7 flex flex-col gap-5"
            style={{
              background: 'linear-gradient(145deg, rgba(54,226,164,0.08), rgba(123,91,255,0.06))',
              border: '1px solid rgba(54,226,164,0.3)',
              boxShadow: '0 0 60px rgba(54,226,164,0.08)',
            }}
          >
            <div className="flex items-start justify-between">
              <div>
                <div
                  className="text-xs font-black uppercase tracking-widest mb-2"
                  style={{ color: '#36e2a4' }}
                >
                  Unbored for Teams
                </div>
                <div className="flex items-end gap-1">
                  <span className="text-5xl font-extrabold text-foreground">$49</span>
                  <span className="text-muted-foreground text-sm font-semibold mb-2">/month</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Up to 25 seats · billed monthly</p>
              </div>
              <div
                className="rounded-xl px-3 py-1.5 text-xs font-black"
                style={{ background: 'rgba(54,226,164,0.15)', color: '#36e2a4' }}
              >
                BEST VALUE
              </div>
            </div>

            <div className="flex flex-col gap-2.5">
              {PLAN_FEATURES.map((feat) => (
                <div key={feat} className="flex items-center gap-3">
                  <div
                    className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-xs"
                    style={{ background: 'rgba(54,226,164,0.2)', color: '#36e2a4' }}
                  >
                    ✓
                  </div>
                  <span className="text-sm text-foreground font-medium">{feat}</span>
                </div>
              ))}
            </div>

            <button
              onClick={handleCTA}
              className="w-full py-4 rounded-2xl text-base font-black"
              style={{
                background: 'linear-gradient(135deg, #36e2a4 0%, #7b5bff 100%)',
                color: '#0a0a0f',
                boxShadow: '0 0 40px rgba(54,226,164,0.25)',
                transition: 'all 150ms ease',
                transform: ctaPressed ? 'scale(0.97)' : 'scale(1)',
              }}
            >
              Start Team Trial →
            </button>
            <p className="text-center text-xs text-muted-foreground">
              No credit card required for trial · cancel anytime
            </p>
          </div>
        </section>

        {/* WAITLIST / CONTACT */}
        <section className="w-full max-w-md mb-8">
          <div
            className="rounded-2xl p-6 text-center"
            style={{
              background: 'linear-gradient(145deg, #16162a, #0f0f1e)',
              border: '1px solid rgba(42,42,69,0.8)',
            }}
          >
            <h3 className="text-lg font-extrabold text-foreground mb-1">
              Want a demo first?
            </h3>
            <p className="text-sm text-muted-foreground mb-5">
              Drop your work email and we&apos;ll send a 5-minute walkthrough video + a 30-day free trial code.
            </p>

            {submitted ? (
              <div
                className="rounded-xl px-4 py-3 text-sm font-bold"
                style={{ background: 'rgba(54,226,164,0.12)', color: '#36e2a4' }}
              >
                ✓ Check your inbox — demo on its way.
              </div>
            ) : (
              <form onSubmit={handleWaitlist} className="flex gap-2">
                <input
                  type="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  placeholder="you@company.com"
                  required
                  className="flex-1 rounded-xl px-4 py-3 text-sm font-medium outline-none"
                  style={{
                    background: 'var(--input)',
                    border: '1px solid var(--border)',
                    color: 'var(--foreground)',
                  }}
                />
                <button
                  type="submit"
                  className="px-5 py-3 rounded-xl text-sm font-black flex-shrink-0"
                  style={{
                    background: 'linear-gradient(135deg, #36e2a4 0%, #7b5bff 100%)',
                    color: '#0a0a0f',
                  }}
                >
                  Send →
                </button>
              </form>
            )}
          </div>
        </section>

        {/* BOTTOM CTA */}
        <section className="w-full max-w-2xl text-center py-8">
          <p className="text-muted-foreground text-sm mb-2">
            Questions? Email us at{' '}
            <a
              href="mailto:support@unboredbutton.com"
              className="font-semibold"
              style={{ color: '#36e2a4' }}
            >
              support@unboredbutton.com
            </a>
          </p>
          <p className="text-xs text-muted-foreground opacity-50">
            Unbored · plays entirely in your browser · no installs · no IT tickets
          </p>
        </section>
      </main>
    </div>
  );
}
